import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./JdAudioAI.css";
import { useNavigate } from 'react-router-dom';
import Navbar from '../About/Navbar';
import Footer from '../About/Footer';
import EnergyVisualizer from "../../User/Questions/ER";

// Azure Speech SDK
const sdk = require("microsoft-cognitiveservices-speech-sdk");

const JdAudioAI = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [transcription, setTranscription] = useState("");
  const [recognisingText, setRecognisingText] = useState("");
  const [status, setStatus] = useState("idle");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [micStatus, setMicStatus] = useState("idle");
  const [micMessage, setMicMessage] = useState("");
  const [speakerStatus, setSpeakerStatus] = useState("idle");
  const [conversationHistory, setConversationHistory] = useState([]);
  const [step, setStep] = useState("not_started");

  // Azure Speech refs
  const recognizerRef = useRef(null);
  const audioStreamRef = useRef(null);
  const transcriptionRef = useRef('');
  const recognitionActiveRef = useRef(false);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const silenceStartRef = useRef(null);
  const monitorIntervalRef = useRef(null);

  const SILENCE_THRESHOLD = 0.05;
  const SILENCE_DURATION = 2000;

  const userId = 'user_' + Math.random().toString(36).substr(2, 9);

  // Initialize Azure STT
  useEffect(() => {
    if (step !== 'not_started' && !showInstructions) {
      initializeAzureSTT();
    }

    return () => {
      cleanupRecognizer();
    };
  }, [step, showInstructions]);

  const initializeAzureSTT = async () => {
    try {
      await cleanupRecognizer();

      // Azure Speech Configuration
      const speechConfig = sdk.SpeechConfig.fromSubscription(
        process.env.REACT_APP_COG_SERVICE_KEY,
        process.env.REACT_APP_COG_SERVICE_LOCATION
      );
      speechConfig.speechRecognitionLanguage = "en-US";

      const constraints = {
        video: false,
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          sampleSize: 16,
          volume: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      };

      const audioStream = await navigator.mediaDevices.getUserMedia(constraints);
      audioStreamRef.current = audioStream;
      
      const audioConfig = sdk.AudioConfig.fromStreamInput(audioStream);
      recognizerRef.current = new sdk.SpeechRecognizer(speechConfig, audioConfig);

      // Setup event handlers
      recognizerRef.current.recognizing = (s, e) => {
        const interimText = e.result.text;
        setRecognisingText(interimText);
      };

      recognizerRef.current.recognized = (s, e) => {
        if (e.result.reason === sdk.ResultReason.RecognizedSpeech) {
          const newText = e.result.text;
          setRecognisingText("");

          transcriptionRef.current = transcriptionRef.current 
            ? `${transcriptionRef.current} ${newText}`
            : newText;

          setTranscription(transcriptionRef.current);
        }
      };

      recognizerRef.current.canceled = (s, e) => {
        console.error("Recognition canceled:", e);
        recognitionActiveRef.current = false;
        setIsRecording(false);
      };

      recognizerRef.current.sessionStopped = (s, e) => {
        recognitionActiveRef.current = false;
        setIsRecording(false);
      };

      // Initialize audio context for silence detection
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContextRef.current.createMediaStreamSource(audioStream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
      source.connect(analyserRef.current);

    } catch (error) {
      console.error("Error initializing Azure STT:", error);
      setMicMessage(`Initialization error: ${error.message}`);
    }
  };

  const startAzureRecognition = async () => {
    if (!recognizerRef.current || recognitionActiveRef.current) {
      return;
    }

    try {
      recognitionActiveRef.current = true;
      await recognizerRef.current.startContinuousRecognitionAsync();
      setMicMessage("");
    } catch (error) {
      console.error("Failed to start recognition:", error);
      setMicMessage(`Failed to start: ${error}`);
      recognitionActiveRef.current = false;
      setIsRecording(false);
    }
  };

  const stopAzureRecognition = async () => {
    if (!recognizerRef.current || !recognitionActiveRef.current) {
      return;
    }

    recognitionActiveRef.current = false;

    return new Promise((resolve) => {
      recognizerRef.current.stopContinuousRecognitionAsync(() => {
        setRecognisingText("");
        resolve();
      });
    });
  };

  const cleanupRecognizer = async () => {
    if (recognizerRef.current) {
      recognitionActiveRef.current = false;
      try {
        await stopAzureRecognition();
        recognizerRef.current.close();
        recognizerRef.current = null;
      } catch (error) {
        console.error("Error cleaning up recognizer:", error);
      }
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  };

  const getRMS = (buffer) => {
    let sumSquares = 0;
    for (let i = 0; i < buffer.length; i++) {
      sumSquares += buffer[i] * buffer[i];
    }
    return Math.sqrt(sumSquares / buffer.length);
  };

  const monitorAudio = () => {
    const buffer = new Float32Array(analyserRef.current.fftSize);
    silenceStartRef.current = Date.now();
    
    if (monitorIntervalRef.current) {
      clearInterval(monitorIntervalRef.current);
    }

    monitorIntervalRef.current = setInterval(async () => {
      analyserRef.current.getFloatTimeDomainData(buffer);
      const rms = getRMS(buffer);
      const now = Date.now();

      if (rms < SILENCE_THRESHOLD) {
        if (!silenceStartRef.current) {
          silenceStartRef.current = now;
        } else if (now - silenceStartRef.current >= SILENCE_DURATION) {
          clearInterval(monitorIntervalRef.current);
          await stopRecording();
        }
      } else {
        silenceStartRef.current = null;
      }
    }, 200);
  };

  const startConversation = async () => {
    try {
      setStatus("processing");
      const response = await axios.post('/api/jd/start-conversation', {
        user_id: userId
      });
      
      setCurrentQuestion(response.data.message);
      setStep(response.data.step);
      setStatus("idle");
      
      await speakText(response.data.message);
      await startRecording();
    } catch (error) {
      setMicMessage('Failed to start conversation: ' + error.message);
      setStatus("idle");
    }
  };

  const startRecording = async () => {
    transcriptionRef.current = '';
    setTranscription('');
    setRecognisingText('');
    setIsRecording(true);
    
    await startAzureRecognition();
    monitorAudio();
  };

  const stopRecording = async () => {
    setIsRecording(false);
    
    if (monitorIntervalRef.current) {
      clearInterval(monitorIntervalRef.current);
    }
    
    await stopAzureRecognition();
    
    if (transcriptionRef.current.trim()) {
      await processTranscription(transcriptionRef.current);
    }
  };

  const processTranscription = async (text) => {
    try {
      setStatus("processing");
      
      const response = await axios.post('/api/jd/process-response', {
        user_id: userId,
        transcription: text
      });
      
      const data = response.data;
      
      if (data.step === 'complete') {
        setStep('complete');
        await speakText(data.message);
        
        // Navigate to JD page with the generated data
        setTimeout(() => {
          navigate("/jd", { state: { jdData: data.job_details } });
        }, 2000);
      } else {
        setCurrentQuestion(data.message);
        setStep(data.step);
        
        await speakText(data.message);
        await startRecording();
      }
      
      setStatus("idle");
    } catch (error) {
      setMicMessage('Failed to process response: ' + error.message);
      setStatus("idle");
    }
  };

  const speakText = async (text) => {
    return new Promise((resolve) => {
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.9;
        utterance.pitch = 1;
        utterance.volume = 1;
        
        setIsSpeaking(true);
        
        utterance.onend = () => {
          setIsSpeaking(false);
          resolve();
        };
        
        utterance.onerror = () => {
          setIsSpeaking(false);
          resolve();
        };
        
        window.speechSynthesis.speak(utterance);
      } else {
        resolve();
      }
    });
  };

  const handleMicTest = async () => {
    setMicStatus("Testing");
    setMicMessage("");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          sampleSize: 16,
          echoCancellation: true
        } 
      });
      
      // Test by recording for 3 seconds
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      source.connect(analyser);
      
      setTimeout(() => {
        stream.getTracks().forEach(track => track.stop());
        audioContext.close();
        setMicStatus("Tested");
        setMicMessage("Microphone test completed successfully.");
      }, 3000);
      
    } catch (error) {
      console.error("Microphone access denied:", error);
      setMicStatus("Error");
      setMicMessage("Microphone access denied.");
    }
  };

  const handleSpeakerTest = () => {
    try {
      const testText = "Speaker test completed successfully. Your speaker is working.";
      speakText(testText);
      setSpeakerStatus("Testing");
      
      setTimeout(() => {
        setSpeakerStatus("Tested");
      }, 2000);
    } catch (error) {
      console.error("Error testing speaker:", error);
      setSpeakerStatus("Error");
    }
  };

  const handleStartJD = () => {
    setShowInstructions(false);
    startConversation();
  };

  return (
    <div className="jd-audioai-page">
      <Navbar />
      
      {showInstructions && (
        <div className="jd-audio-check-overlay">
          <div className="jd-audio-check-modal">
            <h2>🎙️ Audio Setup</h2>
            <p>Please test your microphone and speaker before starting.</p>

            <div className="jd-audio-check-test">
              <button onClick={handleMicTest} className="jd-audio-check-btn">
                {micStatus === "Testing" ? "Testing Microphone..." : "🎤 Test Microphone"}
              </button>
              <div className={`jd-audio-check-status ${micStatus.toLowerCase()}`}>
                {micMessage}
              </div>
            </div>

            <div className="jd-audio-check-test">
              <button onClick={handleSpeakerTest} className="jd-audio-check-btn">
                {speakerStatus === "Testing" ? "Testing Speaker..." : "🔊 Test Speaker"}
              </button>
              <div className={`jd-audio-check-status ${speakerStatus.toLowerCase()}`}>
                {speakerStatus === "Tested" && "Speaker is working!"}
              </div>
            </div>

            {(micStatus === "Tested" && speakerStatus === "Tested") && (
              <button
                className="jd-audio-check-start"
                onClick={handleStartJD}
              >
                ✅ Generate JD
              </button>
            )}
          </div>
        </div>
      )}

      {!showInstructions && (
        <div className="ai-layout-audio">
          <div className="ai-interview-env">
            <h1 className="ai-interview-title">AI Interviewer: Job Description Creation</h1>
            <div className="ai-avatar-section">
              <div className="ai-avatar big">
                <div className="visualizer-container">
                  <EnergyVisualizer isSpeaking={isSpeaking} isRecording={isRecording} timeLeft={-1}/>
                  <div className={`status-label ${isSpeaking ? "speaking-label" : isRecording ? "listening-label" : "thinking-label"}`}>
                    {isSpeaking ? "🎤 Speaking..." : isRecording ? "🎧 Listening..." : "💭 Thinking..."}
                  </div>
                </div>
              </div>
            </div>
            <div className="ai-conversation-area">
              <div className="ai-bubble">
                <span className="ai-label">AI:</span> {currentQuestion}
              </div>
              <div className="user-bubble">
                <span className="user-label">You Said:</span>
                <span className="user-text">
                  {recognisingText || transcription || "Listening..."}
                </span>
              </div>
            </div>
            
            {status === "processing" && (
              <div className="processing-indicator">
                Processing your response...
              </div>
            )}
          </div>
          
          <div className="tips-section-audio">
            <h3>🎧 Answer Clearly, Get a Better Job Description:</h3>
            <ul>
              <li>Speak clearly and naturally — the AI is listening in real time</li>
              <li>Answer one question at a time without rushing</li>
              <li>Use specific job titles and experience levels</li>
              <li>Include salary range and location details if known</li>
              <li>Summarize roles and responsibilities in short, clear phrases</li>
              <li>Mention key required skills or technologies</li>
              <li>Say "remote," "hybrid," or "on-site" for work arrangement</li>
              <li>The system will automatically detect when you stop speaking</li>
            </ul>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default JdAudioAI;